import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../contexts/AuthContext';
import { useService } from '../contexts/ServiceContext';
import { useModal } from '../contexts/ModalContext';
import { useTenant } from '../contexts/TenantContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../flags';
import { useTenantPath } from './useTenantPath';

/**
 * Hook to check if the user has accepted terms and show modal if not.
 * This should be called at the application level to gate any entry to NGW.
 *
 * @returns {boolean | null} - null while checking, true if accepted, false if declined
 */
export function useUserTermsCheck(): boolean | null {
  const { session } = useAuth();
  const { saveAccount } = useService();
  const { showModal } = useModal();
  const { getConfigValue } = useTenant();
  const { hasFeature } = useFeatureFlags();
  const { getPath } = useTenantPath();

  const [termsAccepted, setTermsAccepted] = useState<boolean | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const hasShownModalRef = useRef(false);

  useEffect(() => {
    const checkUserTerms = async () => {
      if (!session) return;

      // If feature flag is not enabled, consider terms as accepted
      if (!hasFeature(FeatureFlags.NGW_USER_TERMS)) {
        setTermsAccepted(true);
        return;
      }

      // Get the actual value from session metadata (from /me endpoint)
      const hasAcceptedUserTerms = session.metadata?.hasAcceptedUserTerms;
      const acceptedVersion = session.metadata?.userTermsVersion;

      // Get current terms version from tenant config
      const currentVersion = getConfigValue<string>(
        'USER_TERMS_VERSION',
        '1.0',
      );

      // Check if user needs to accept terms:
      const needsToAcceptTerms =
        !hasAcceptedUserTerms || acceptedVersion !== currentVersion;

      if (!needsToAcceptTerms) {
        setTermsAccepted(true);
        hasShownModalRef.current = false; // Reset for next check
      } else if (!hasShownModalRef.current) {
        // User needs to accept terms (either new user or version mismatch)
        hasShownModalRef.current = true;
        setIsModalOpen(true);

        // Get terms text and version from tenant config
        const termsText = getConfigValue<string>(
          'USER_TERMS_TEXT',
          'Please review and accept our Terms of Service to continue using the Listing Management platform.',
        );
        const termsVersion = getConfigValue<string>(
          'USER_TERMS_VERSION',
          '1.0',
        );

        showModal({
          title: 'Terms of Service',
          content: (
            <div>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {termsText}
              </ReactMarkdown>
            </div>
          ),
          width: 'md',
          confirmText: 'Accept',
          cancelText: 'Decline',
          switchLayout: false,
          disableBackdropClick: true,
          onConfirm: async () => {
            try {
              await saveAccount({
                hasAcceptedUserTerms: true,
                userTermsAcceptedAt: new Date().toISOString(),
                userTermsVersion: termsVersion,
              });
              // Small delay to ensure modal is fully dismissed before rendering routes
              setTimeout(() => {
                setIsModalOpen(false);
                setTermsAccepted(true);
              }, 150);
            } catch (error) {
              console.error('Error saving terms acceptance:', error);
              setIsModalOpen(false);
            }
          },
          onCancel: async () => {
            // User declined terms, redirect to overview
            setIsModalOpen(false);
            setTermsAccepted(false);
            const overviewPath = getPath('/');
            window.location.href = overviewPath;
          },
        });
      }
    };

    checkUserTerms();
  }, [session, saveAccount, showModal, getPath, getConfigValue, hasFeature]);

  // Return null if modal is open (blocking) or if check hasn't completed
  // Return the actual value once modal is closed and check is complete
  return isModalOpen ? null : termsAccepted;
}
